import React, { useCallback, useEffect, useState } from 'react';
import {
  Star,
  Eye,
  EyeOff,
  Trash2,
  ChevronUp,
  ChevronDown,
  Plus,
  Save,
  X,
  Loader2,
  SlidersHorizontal,
} from 'lucide-react';
import { apiClient } from '../../lib/apiClient';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import PasteImageUpload, { MAX_SHOWCASE_IMAGES } from '../../components/admin/PasteImageUpload';
import FilterDrawer from '../../components/admin/FilterDrawer';

/**
 * Showcase Directory admin.
 *
 * Manages the portfolio shown on the home page and /apps: create, edit,
 * reorder, publish/unpublish, star for the home-page spotlight, and delete.
 *
 * Ordering uses explicit up/down controls rather than drag-and-drop. Dragging
 * is nicer with a mouse and considerably worse with a keyboard or on a touch
 * screen, and this list is short enough that two buttons are quicker anyway.
 */

const EMPTY = {
  name: '',
  tagline: '',
  short_description: '',
  url: '',
  category: '',
  industries: [],
  project_types: [],
  features: [],
  screenshots: [],
  published: true,
  featured: false,
};

const AdminShowcase = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // project object or EMPTY for new
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState({ industry: [], project_type: [] });
  const [filtersOpen, setFiltersOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/showcase/');
      setProjects(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(
        err?.response?.status === 401 || err?.response?.status === 403
          ? 'You need to be signed in as staff to manage the directory.'
          : 'Could not load the directory.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Filter options are managed separately, so fetch them for the editor's
  // multi-selects. Failing is survivable — the rest of the form still works.
  // Extracted so the drawer can refresh these the moment it changes something,
  // rather than leaving the editor showing a stale set until a reload.
  const loadOptions = useCallback(async () => {
    try {
      const res = await apiClient.get('/public/showcase/filters');
      setOptions({
        industry: res.data?.industry || [],
        project_type: res.data?.project_type || [],
      });
    } catch (err) {
      /* leave the selects empty rather than blocking the editor */
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  /** Add or remove one value from a project's axis array. */
  const toggleAxis = (key, name) =>
    setEditing((prev) => {
      const current = prev[key] || [];
      return {
        ...prev,
        [key]: current.includes(name)
          ? current.filter((v) => v !== name)
          : [...current, name],
      };
    });

  /** Optimistic toggle: flip locally, revert if the server disagrees. */
  const toggle = async (project, field) => {
    const next = !project[field];
    setProjects((prev) =>
      prev.map((p) => (p.id === project.id ? { ...p, [field]: next } : p)),
    );
    try {
      const path = field === 'published' ? 'publish' : 'feature';
      await apiClient.patch(`/showcase/${project.id}/${path}?${field}=${next}`);
    } catch (err) {
      setProjects((prev) =>
        prev.map((p) => (p.id === project.id ? { ...p, [field]: !next } : p)),
      );
      setError(`Could not update "${project.name}".`);
    }
  };

  const move = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= projects.length) return;

    const next = [...projects];
    [next[index], next[target]] = [next[target], next[index]];
    setProjects(next);

    try {
      await apiClient.post('/showcase/reorder', { ids: next.map((p) => p.id) });
    } catch (err) {
      setError('Could not save the new order.');
      load();
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!editing?.name?.trim()) return;

    setSaving(true);
    setError(null);
    try {
      if (editing.id) {
        await apiClient.put(`/showcase/${editing.id}`, editing);
      } else {
        await apiClient.post('/showcase/', editing);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (project) => {
    // Deliberate confirm: delete is not recoverable from this screen.
    if (!window.confirm(`Delete "${project.name}" from the directory? This cannot be undone.`)) {
      return;
    }
    try {
      await apiClient.delete(`/showcase/${project.id}`);
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
    } catch (err) {
      setError(`Could not delete "${project.name}".`);
    }
  };

  const field = (label, key, type = 'text') => (
    <div>
      <label className="block text-gray-500 text-xs mb-1">{label}</label>
      {type === 'textarea' ? (
        <textarea
          rows={2}
          value={editing[key] || ''}
          onChange={(e) => setEditing({ ...editing, [key]: e.target.value })}
          className="w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 text-sm focus:border-[#FF6B00] outline-none resize-none"
        />
      ) : (
        <input
          type={type}
          value={editing[key] || ''}
          onChange={(e) => setEditing({ ...editing, [key]: e.target.value })}
          className="w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 text-sm focus:border-[#FF6B00] outline-none"
        />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Showcase Directory</h1>
            <p className="text-gray-500 text-sm mt-1">
              Starred projects appear in the home-page spotlight. Up to{' '}
              {MAX_SHOWCASE_IMAGES} images each.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setFiltersOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:border-[#FF6B00] hover:text-[#FF6B00] font-semibold text-sm transition-colors"
              data-testid="showcase-manage-filters"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
            </button>
            <button
              type="button"
              onClick={() => setEditing({ ...EMPTY })}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#ff8124] text-[#1a1c1b] font-semibold text-sm"
              data-testid="showcase-add"
            >
              <Plus className="w-4 h-4" />
              Add project
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Editor */}
        {editing && (
          <form
            onSubmit={save}
            className="mb-6 p-5 rounded-xl border border-[#FF6B00]/40 bg-white"
            data-testid="showcase-editor"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-gray-900 font-semibold">
                {editing.id ? `Edit ${editing.name}` : 'New project'}
              </h2>
              <button
                type="button"
                onClick={() => setEditing(null)}
                aria-label="Close editor"
                className="text-gray-500 hover:text-gray-900"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-3 mb-3">
              {field('Name *', 'name')}
              {field('Category', 'category')}
              {field('Tagline', 'tagline')}
              {field('Live URL', 'url', 'url')}
            </div>

            <div className="mb-4">{field('Short description', 'short_description', 'textarea')}</div>

            {/* Both axes accept several values — a CRM that is also full stack,
                serving more than one sector. */}
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              {[
                { key: 'industries', optKey: 'industry', label: 'Industries' },
                { key: 'project_types', optKey: 'project_type', label: 'Types' },
              ].map((axis) => (
                <div key={axis.key}>
                  <label className="block text-gray-500 text-xs mb-2">
                    {axis.label}
                    <span className="ml-1 text-gray-400">(choose any)</span>
                  </label>
                  {options[axis.optKey].length === 0 ? (
                    <p className="text-gray-400 text-xs">
                      No options yet — add some in filter settings.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {options[axis.optKey].map((o) => {
                        const on = (editing[axis.key] || []).includes(o.name);
                        return (
                          <button
                            key={o.id || o.name}
                            type="button"
                            onClick={() => toggleAxis(axis.key, o.name)}
                            aria-pressed={on}
                            data-testid={`axis-${axis.key}-${o.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                              on
                                ? 'border-[#FF6B00] bg-[#FF6B00]/10 text-[#FF6B00]'
                                : 'border-gray-200 text-gray-700 hover:border-gray-400'
                            }`}
                          >
                            {o.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mb-4">
              <PasteImageUpload
                label="Screenshots"
                folder="showcase"
                value={editing.screenshots || []}
                onChange={(screenshots) => setEditing({ ...editing, screenshots })}
              />
            </div>

            <div className="flex items-center gap-4">
              <button
                type="submit"
                disabled={saving || !editing.name?.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#ff8124] disabled:opacity-50 text-[#1a1c1b] font-semibold text-sm"
                data-testid="showcase-save"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Saving…' : 'Save'}
              </button>
              <label className="flex items-center gap-2 text-gray-700 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-[#FF6B00]"
                  checked={!!editing.published}
                  onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                />
                Published
              </label>
              <label className="flex items-center gap-2 text-gray-700 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-[#FF6B00]"
                  checked={!!editing.featured}
                  onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}
                />
                Starred
              </label>
            </div>
          </form>
        )}

        {/* List */}
        {loading ? (
          <p className="text-gray-500 text-sm">Loading…</p>
        ) : projects.length === 0 ? (
          <p className="text-gray-500 text-sm">No projects yet. Add the first one.</p>
        ) : (
          <div className="space-y-2">
            {projects.map((p, i) => (
              <div
                key={p.id}
                className={`flex items-center gap-3 p-3 rounded-xl border bg-white ${
                  p.published ? 'border-gray-200' : 'border-gray-200 opacity-60'
                }`}
                data-testid={`showcase-row-${p.slug}`}
              >
                {/* Order */}
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    aria-label={`Move ${p.name} up`}
                    className="text-gray-500 hover:text-[#FF6B00] disabled:opacity-30"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, 1)}
                    disabled={i === projects.length - 1}
                    aria-label={`Move ${p.name} down`}
                    className="text-gray-500 hover:text-[#FF6B00] disabled:opacity-30"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>

                {p.screenshots?.[0] ? (
                  <img
                    src={resolveMediaUrl(p.screenshots[0])}
                    alt=""
                    className="w-16 h-10 object-cover rounded border border-gray-200"
                  />
                ) : (
                  <div className="w-16 h-10 rounded border border-gray-200 bg-gray-50" />
                )}

                <div className="flex-1 min-w-0">
                  <p className="text-gray-900 text-sm font-semibold truncate">{p.name}</p>
                  {/* Show the real tags rather than the legacy category, so the
                      list reflects what the public filters actually use. */}
                  <p className="text-gray-500 text-xs truncate">
                    {[...(p.industries || []), ...(p.project_types || [])].join(' · ') ||
                      'Untagged'}
                    {p.screenshots?.length ? ` · ${p.screenshots.length} image${p.screenshots.length === 1 ? '' : 's'}` : ''}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => toggle(p, 'featured')}
                  aria-pressed={!!p.featured}
                  aria-label={`${p.featured ? 'Unstar' : 'Star'} ${p.name}`}
                  title="Show in the home-page spotlight"
                  className={p.featured ? 'text-[#f8d47a]' : 'text-gray-500 hover:text-[#f8d47a]'}
                >
                  <Star className="w-4 h-4" fill={p.featured ? 'currentColor' : 'none'} />
                </button>

                <button
                  type="button"
                  onClick={() => toggle(p, 'published')}
                  aria-pressed={!!p.published}
                  aria-label={`${p.published ? 'Unpublish' : 'Publish'} ${p.name}`}
                  className={p.published ? 'text-[#FF6B00]' : 'text-gray-500 hover:text-gray-900'}
                >
                  {p.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>

                <button
                  type="button"
                  onClick={() => setEditing({ ...p })}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 text-xs hover:border-[#FF6B00] hover:text-[#FF6B00]"
                >
                  Edit
                </button>

                <button
                  type="button"
                  onClick={() => remove(p)}
                  aria-label={`Delete ${p.name}`}
                  className="text-gray-500 hover:text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Refreshing on every change keeps the editor's tag chips in step with
          the drawer, instead of showing a stale set until the page reloads.
          The project list reloads too, since a rename or delete rewrites the
          tags stored on projects. */}
      <FilterDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onChanged={() => {
          loadOptions();
          load();
        }}
      />
    </div>
  );
};

export default AdminShowcase;
